import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client.js';
import type {
  AdminAccountRow, AdminIngestToken, AdminIngestTokenCreated,
  ModelSummary, TwoFactorStatus, User,
} from '@wml-crm/shared';

type Tab = 'users' | '2fa' | 'tokens' | 'accounts';

interface Props {
  me: User;
}

export function AdminPage({ me }: Props) {
  const [tab, setTab] = useState<Tab>('users');
  const isSuperAdmin = me.email && me.role === 'admin';

  if (!isSuperAdmin) {
    return (
      <div className="home-inner">
        <div className="empty-state">Доступ в админку только у супер-админов.</div>
      </div>
    );
  }

  return (
    <div className="home-inner">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>Админка</h1>
        <div className="range-tabs">
          <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>Сотрудники</button>
          <button className={tab === '2fa' ? 'active' : ''} onClick={() => setTab('2fa')}>2FA-код</button>
          <button className={tab === 'tokens' ? 'active' : ''} onClick={() => setTab('tokens')}>Ingest-токены</button>
          <button className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>Аккаунты</button>
        </div>
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === '2fa' && <TwoFactorTab />}
      {tab === 'tokens' && <TokensTab />}
      {tab === 'accounts' && <AccountsTab />}
    </div>
  );
}

// ============ USERS ============

function UsersTab() {
  const [users, setUsers] = useState<User[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'member' as 'admin' | 'member' });
  const [editing, setEditing] = useState<User | null>(null);

  async function load() {
    try { setUsers(await api<User[]>('/api/admin/users')); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api('/api/admin/users', { method: 'POST', body: JSON.stringify(form) });
      setForm({ name: '', email: '', password: '', role: 'member' });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setBusy(false); }
  }

  async function toggleSuspend(u: User) {
    await api(`/api/admin/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ suspended: !u.suspended }) });
    await load();
  }

  async function toggleAnalyzed(u: User) {
    await api(`/api/admin/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ canViewAnalyzed: !u.canViewAnalyzed }) });
    await load();
  }

  async function removeUser(u: User) {
    if (!confirm(`Удалить ${u.email}?`)) return;
    await api(`/api/admin/users/${u.id}`, { method: 'DELETE' });
    await load();
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>Создать сотрудника</h3>
        <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto', gap: 8 }}>
          <input className="admin-input" placeholder="Имя" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="admin-input" placeholder="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className="admin-input" placeholder="пароль (≥6)" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <select className="admin-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'member' })}>
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>
          <button className="btn-primary" disabled={busy} style={{ width: 'auto', padding: '0 16px' }}>Создать</button>
        </form>
        {err && <div className="error">{err}</div>}
      </div>

      <div className="card">
        <table className="admin-table">
          <thead><tr><th>Имя</th><th>Email</th><th>Роль</th><th>Статус</th><th title="Доступ к вкладке «Проанализированные модели»">Проанализ. модели</th><th>Последний вход</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{u.suspended ? <span style={{ color: 'var(--accent)' }}>приостановлен</span> : 'активен'}</td>
                <td>
                  {u.isSuperAdmin ? (
                    <span style={{ color: 'var(--text-subtle)' }} title="Супер-админ видит вкладку всегда">всегда</span>
                  ) : (
                    <input type="checkbox" checked={u.canViewAnalyzed} onChange={() => toggleAnalyzed(u)} />
                  )}
                </td>
                <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ru-RU') : '—'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="link-btn" onClick={() => setEditing(u)}>изменить</button>
                  {' · '}
                  <button className="link-btn" onClick={() => toggleSuspend(u)}>{u.suspended ? 'разморозить' : 'приостановить'}</button>
                  {' · '}
                  <button className="link-btn danger" onClick={() => removeUser(u)}>удалить</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

// ============ EDIT USER MODAL (админ меняет имя/email/пароль) ============

function EditUserModal({ user, onClose, onSaved }: { user: User; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const payload: Record<string, string> = {};
    if (name !== user.name) payload.name = name;
    if (email !== user.email) payload.email = email;
    if (password) payload.password = password;
    if (Object.keys(payload).length === 0) { onClose(); return; }
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Изменить сотрудника</div>
        <form onSubmit={save} style={{ display: 'grid', gap: 10 }}>
          <label className="field">
            <span>Имя</span>
            <input className="admin-input" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span>Email</span>
            <input className="admin-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="field">
            <span>Новый пароль (оставь пустым чтобы не менять)</span>
            <input className="admin-input" type="password" placeholder="≥6 символов" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          {err && <div className="error">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={busy} style={{ width: 'auto', padding: '0 20px' }}>Сохранить</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ 2FA ============

function TwoFactorTab() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loop() {
      while (!cancelled) {
        try {
          const s = await api<TwoFactorStatus>('/api/admin/2fa');
          if (!cancelled) setStatus(s);
          await new Promise((r) => setTimeout(r, Math.max(1000, s.msRemaining + 200)));
        } catch {
          await new Promise((r) => setTimeout(r, 5000));
        }
      }
    }
    loop();
    return () => { cancelled = true; };
  }, []);

  async function copyCode() {
    if (!status?.code) return;
    try {
      await navigator.clipboard.writeText(status.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard недоступен — тихо */
    }
  }

  return (
    <div className="card" style={{ textAlign: 'center', padding: 40 }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
        Текущий 6-значный код для сотрудников:
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ fontSize: 56, fontWeight: 700, letterSpacing: 8, color: 'var(--accent)' }}>
          {status?.code ?? '——————'}
        </div>
        <button
          className="copy-2fa-btn"
          onClick={copyCode}
          disabled={!status?.code}
          title={copied ? 'Скопировано' : 'Скопировать код'}
        >
          {copied ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15V5a2 2 0 0 1 2-2h10" />
            </svg>
          )}
        </button>
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
        Обновляется каждые {status?.periodSeconds ?? 30} сек.
      </div>
    </div>
  );
}

// ============ INGEST TOKENS ============

function TokensTab() {
  const [tokens, setTokens] = useState<AdminIngestToken[]>([]);
  const [name, setName] = useState('');
  const [lastCreated, setLastCreated] = useState<AdminIngestTokenCreated | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try { setTokens(await api<AdminIngestToken[]>('/api/admin/ingest-tokens')); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
  }
  useEffect(() => { load(); }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    try {
      const t = await api<AdminIngestTokenCreated>('/api/admin/ingest-tokens', {
        method: 'POST', body: JSON.stringify({ name }),
      });
      setLastCreated(t);
      setName('');
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
  }

  async function remove(id: string) {
    if (!confirm('Удалить токен?')) return;
    await api(`/api/admin/ingest-tokens/${id}`, { method: 'DELETE' });
    await load();
  }

  return (
    <>
      {lastCreated && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--accent)' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Токен создан — скопируй сейчас, потом не покажем:</div>
          <code style={{ display: 'block', padding: 10, background: 'var(--bg)', borderRadius: 8, wordBreak: 'break-all' }}>
            {lastCreated.rawToken}
          </code>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <form onSubmit={create} style={{ display: 'flex', gap: 8 }}>
          <input className="admin-input" placeholder="Название (например: stats-dm-prod)" required value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn-primary" style={{ width: 'auto', padding: '0 16px' }}>Выпустить токен</button>
        </form>
        {err && <div className="error">{err}</div>}
      </div>

      <div className="card">
        <table className="admin-table">
          <thead><tr><th>Название</th><th>Префикс</th><th>Последнее использование</th><th>Создан</th><th></th></tr></thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td><code>{t.prefix}…</code></td>
                <td>{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString('ru-RU') : 'ни разу'}</td>
                <td>{new Date(t.createdAt).toLocaleDateString('ru-RU')}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="link-btn danger" onClick={() => remove(t.id)}>удалить</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ============ ACCOUNTS + MOVE ============

function AccountsTab() {
  const [accounts, setAccounts] = useState<AdminAccountRow[]>([]);
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [q, setQ] = useState('');
  const [moving, setMoving] = useState<AdminAccountRow | null>(null);

  async function load() {
    const [a, m] = await Promise.all([
      api<AdminAccountRow[]>('/api/admin/accounts'),
      api<ModelSummary[]>('/api/models'),
    ]);
    setAccounts(a);
    setModels(m);
  }
  useEffect(() => { load(); }, []);

  const filtered = q
    ? accounts.filter((a) => (a.slaveName + ' ' + a.model.displayName).toLowerCase().includes(q.toLowerCase()))
    : accounts;

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <input className="admin-input" placeholder="Фильтр по имени слейва или модели…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%' }} />
      </div>

      <div className="card">
        <table className="admin-table">
          <thead><tr><th>Слейв (Stats DM)</th><th>Handle</th><th>Модель в CRM</th><th>Постов</th><th></th></tr></thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id}>
                <td>{a.slaveName}</td>
                <td>{a.handle ? '@' + a.handle : '—'}</td>
                <td>{a.model.displayName}</td>
                <td>{a.postCount}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="link-btn" onClick={() => setMoving(a)}>перенести →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {moving && (
        <MoveAccountModal
          account={moving}
          models={models}
          onClose={() => setMoving(null)}
          onDone={async () => { setMoving(null); await load(); }}
        />
      )}
    </>
  );
}

function MoveAccountModal({
  account, models, onClose, onDone,
}: {
  account: AdminAccountRow;
  models: ModelSummary[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [targetModelId, setTargetModelId] = useState<string>(models[0]?.id ?? '');
  const [newModelName, setNewModelName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = { accountId: account.id, reason };
      if (mode === 'existing') body.targetModelId = targetModelId;
      else body.targetModelName = newModelName;
      await api('/api/admin/move-account', { method: 'POST', body: JSON.stringify(body) });
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-head">
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Перенести акк на другую модель</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{account.slaveName} · сейчас у «{account.model.displayName}»</div>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <form className="drawer-body" onSubmit={submit}>
          <div className="range-tabs" style={{ alignSelf: 'flex-start' }}>
            <button type="button" className={mode === 'existing' ? 'active' : ''} onClick={() => setMode('existing')}>Существующая модель</button>
            <button type="button" className={mode === 'new' ? 'active' : ''} onClick={() => setMode('new')}>Создать новую</button>
          </div>

          {mode === 'existing' ? (
            <div className="field">
              <label>Модель</label>
              <select className="admin-input" value={targetModelId} onChange={(e) => setTargetModelId(e.target.value)}>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>{m.displayName} ({m.accountCount} акк.)</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="field">
              <label>Название новой модели</label>
              <input className="admin-input" required value={newModelName} onChange={(e) => setNewModelName(e.target.value)} />
            </div>
          )}

          <div className="field">
            <label>Причина (для аудита, не обязательно)</label>
            <input className="admin-input" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>

          <button className="btn-primary" disabled={busy}>{busy ? 'Переношу…' : 'Перенести'}</button>
          {err && <div className="error">{err}</div>}
        </form>
      </div>
    </>
  );
}
